/* @typescript-eslint/no-unsafe-assignment */
/* @typescript-eslint/no-unsafe-return */
/* @typescript-eslint/no-unsafe-call */
import { useEffect, useRef, useState } from 'react';

import scrollIntoView from 'scroll-into-view';

import { BladeNameT as BladeNameType } from './app/navigation/types';

import Blades from './app/navigation/blades/Blades';
import {
    addBlade,
    completeBladeNavigation,
    removeExistingBladesBefore,
    selectActivatingBladeName,
    selectAppInitState,
    selectBlades,
    selectCurrentBladeAllowNavigateToPrevious,
    startAppInit,
    startBladeNavigation,
    startPrevBladeNavigation,
} from './app/navigation/navigationSlice';
import usePrevious from './utils/usePrevious';
import { useAppDispatch, useAppSelector } from './stories/hooks';

type RefT = HTMLDivElement | null;

type RefsT<T extends string> = {
    [key in T]?: RefT;
}

type ReturnT<T> = {
    blades: T[];
    focusedBlade: T | null;
    navigateToBlade: (str: T, isPrev?: boolean) => void;
    attachRef: (name: T) => (bladeRef: RefT) => void;
    activatingBlade: T | null;
    bladeHeaderMain: string;
    bladeHeaderContent: string;
};

type RunAniT<T> = {
    blade: T;
}

const useBladeFlow = <T extends string, >(
    initBlade: T,
): ReturnT<T> => {
    const dispatch = useAppDispatch();
    const isInited = useAppSelector(selectAppInitState);
    const blades = useAppSelector(selectBlades) as T[];
    const prevBlades = usePrevious(blades);
    const activatingBlade = useAppSelector(selectActivatingBladeName) as T;
    const isAllowNavigateToPrevious = useAppSelector((state) => selectCurrentBladeAllowNavigateToPrevious(state, activatingBlade as BladeNameType));
    const [focusedBlade, setFocusedBlade] = useState<T | null>(null);
    const [bladeHeaderMain, setBladeHeaderMain] = useState<string>('');
    const [bladeHeaderContent, setBladeHeaderContent] = useState<string>('');
    const cancel = useRef<null | ScrollCancelT>(null);
    const ref = useRef<RefsT<T>>({});

    // Find the new added blade
    const currentLastBlade = blades.find((blade) => prevBlades.indexOf(blade) === -1);

    const navigateToBlade = (bladeName: T, isPrev = false) => {
        if (isPrev) {
            dispatch(startPrevBladeNavigation(bladeName));
        } else {
            dispatch(startBladeNavigation(bladeName));
        }
    };

    const attachRef = (bladeName: T) => (
        (bladeRef: RefT) => {
            ref.current[bladeName] = bladeRef;
        }
    );

    const runAnimation = ({ blade }: RunAniT<T>) => {
        cancel.current = scrollIntoView(
            ref.current[blade],
            {
                time: 300,
                align: {
                    topOffset: 80,
                },
            },
            (type) => {
                if (type === 'complete') {
                    setFocusedBlade(blade);
                    dispatch(completeBladeNavigation(blade));
                }
                if (type === 'canceled') {
                    dispatch(completeBladeNavigation(blade));
                }
            },
        );
    };

    useEffect(() => {
        if (activatingBlade) {
            const isExistingBlade = !!blades.find((bladeName) => bladeName === activatingBlade);

            if (isExistingBlade) {
                runAnimation({
                    blade: activatingBlade,
                });
            } else {
                if (!isAllowNavigateToPrevious) {
                    dispatch(removeExistingBladesBefore(activatingBlade as string));
                }
                dispatch(addBlade(activatingBlade));
            }
            setBladeHeaderMain(Blades[activatingBlade as BladeNameType].headerMain);
            setBladeHeaderContent(Blades[activatingBlade as BladeNameType].headerContent);
        }
    }, [activatingBlade]);

    useEffect(() => {
        // Scroll to the last blade in the blades array
        if (currentLastBlade) {
            runAnimation({
                blade: currentLastBlade,
            });
        }
    }, [currentLastBlade]);

    useEffect(() => {
        if (!isInited) {
            dispatch(startAppInit());
        }
        navigateToBlade(initBlade);
    }, []);

    useEffect(() => () => {
        // cancel scroller
        if (cancel.current) cancel.current();
    }, []);

    return {
        blades,
        navigateToBlade,
        attachRef,
        focusedBlade,
        activatingBlade,
        bladeHeaderMain,
        bladeHeaderContent,
    };
};

export default useBladeFlow;
